import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getButtonText,
  getResponseText,
  getResponseErrorMessage,
} from './TestActionFunctions';
import DynamicJSONCard from './SchemaFormTemplates/DynamicJSONCard';
import {
  TestActionLayout,
  TestActionVerdict,
  testResponsePreClasses,
} from './TestActionLayout';
import { buttonClasses } from './tailwindClassnames';

const customWidgets: Record<string, React.ComponentType<any>> = {
  DynamicJSONCard: DynamicJSONCard,
};

type TestActionProps = {
  actionId: string;
  actionValue: any;
  onTestAction: Function;
  isLoading: boolean;
  response: any | null;
  buttonText?: string;
  uiSchema?: Record<string, any>;
  formTitle?: string;
};

/** Test action for a saved integration — it has an id, so the action runs
 *  against the stored configuration. Draft equivalent: DraftTestActionButton. */
const TestAction: React.FC<TestActionProps> = ({
  actionId,
  actionValue,
  onTestAction,
  isLoading,
  response,
  buttonText,
  uiSchema,
  formTitle,
}) => {
  const { t } = useTranslation();

  // A response may name the widget it wants rendered with.
  const getWidgetComponent = () => {
    if (!response || !response.ui || !response.ui.widget) return null;
    return customWidgets[response.ui.widget] || null;
  };

  const renderResponseDetails = (isSuccess: boolean | null) => {
    const WidgetComponent = getWidgetComponent();

    if (WidgetComponent && response.data) {
      if (Array.isArray(response.data)) {
        return response.data.map((item: any, index: number) => (
          <WidgetComponent key={index} {...item} />
        ));
      }
      if (typeof response.data === 'object') {
        // Object data — the common case for section-based widgets.
        return Object.entries(response.data).map(([key, value]) => (
          <WidgetComponent key={key} header={key} content={value} />
        ));
      }
    }

    return (
      <pre className={testResponsePreClasses(isSuccess)}>
        {JSON.stringify(response, null, 2)}
      </pre>
    );
  };

  const verdict: TestActionVerdict | null = (() => {
    if (!response) return null;
    const [message, isSuccess] = getResponseText(actionValue, response, t);
    // Credentials are a verdict, not a payload: success has nothing worth
    // expanding, and failure has only its message. Every other action returns
    // data the operator needs to inspect, so those keep the full response.
    if (actionValue === 'auth') {
      const errorMessage = isSuccess
        ? undefined
        : getResponseErrorMessage(response);
      return {
        message,
        isSuccess,
        details: errorMessage ? (
          <pre
            className={`${testResponsePreClasses(false)} whitespace-pre-wrap break-words`}
          >
            {errorMessage}
          </pre>
        ) : undefined,
      };
    }
    return { message, isSuccess, details: renderResponseDetails(isSuccess) };
  })();

  return (
    <TestActionLayout
      formTitle={formTitle}
      isLoading={isLoading}
      verdict={verdict}
      button={
        <button
          className={`opacity-100 ${buttonClasses.headerActionGreen}`}
          onClick={() => onTestAction(actionId, actionValue)}
        >
          {uiSchema?.['ui:action_button_label'] ||
            buttonText ||
            getButtonText(actionValue, t)}
        </button>
      }
    />
  );
};

export default TestAction;
